import { meetingService } from "@/services/meetingService";
import type { Document } from "@repo/types";
import { Button } from "@repo/ui/components/Base/Button";
import { ConfirmDialog } from "@repo/ui/components/Base/ConfirmDialog";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";
import { SelectItem } from "@repo/ui/components/Base/Select";
import { SelectInput } from "@repo/ui/components/Base/SelectInput";
import { ArrowBigRightDashIcon } from "lucide-react";
import { Fragment, useMemo, useState } from "react";
import { ErrorsDialog } from "../meetings/ErrorsDialog";
import { formatDate } from "date-fns";
import { formatTZ } from "@repo/shared/utils";
import { useTenantStore } from "@/stores/tenantStore";
import { DATETIME_FORMAT } from "@/lib/constants";
import { useDocumentList } from "@/hooks/queryHooks/documentHooks";
import { useMeetingList } from "@/hooks/queryHooks/meetingHooks";
import { useTeamList } from "@/hooks/queryHooks/teamHooks";
import { useLaserficheGetIntegration } from "@/hooks/queryHooks/laserficheIntegrationHooks";
import { documentRepo } from "@/repositories/documentRepo";

export interface LaserfichePushHistoryFormProps {
  tenantId: string;
}

export const LaserfichePushHistoryForm = ({
  tenantId,
}: LaserfichePushHistoryFormProps) => {
  const { data: teams } = useTeamList(tenantId);
  const { data: meetings } = useMeetingList(tenantId, "org");
  const { data: documents } = useDocumentList(tenantId, "org");
  const tenant = useTenantStore((s) => s.tenant);

  const [teamId, setTeamId] = useState("all");
  const [meetingId, setMeetingId] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [errorsOpen, setErrorsOpen] = useState(false);
  const [confirmDetails, setConfirmDetails] = useState<React.ReactNode>(null);
  const [errors, setErrors] = useState("");

  // find Laserfiche connection for the selected meeting's team
  const meeting = useMemo(
    () => meetings?.find((meeting) => meeting.id === meetingId),
    [meetings, meetingId]
  );
  const { data: defaultLaserfiche } = useLaserficheGetIntegration(
    tenantId,
    null
  );
  const { data: teamLaserfiche } = useLaserficheGetIntegration(
    tenantId,
    meeting?.teamId ?? null
  );
  const laserfiche = teamLaserfiche?.connection
    ? teamLaserfiche
    : defaultLaserfiche;
  const isDefault = laserfiche === defaultLaserfiche;

  // filter meetings by selected team
  const filteredMeetings = useMemo(
    () =>
      meetings?.filter(
        (meeting) =>
          meeting.agendaUrl && (teamId === "all" || meeting.teamId === teamId)
      ) ?? [],
    [meetings, teamId]
  );

  const handleOpenConfirm = useFutureCallback(() => {
    if (
      !teams ||
      !meeting ||
      !laserfiche?.connection ||
      !laserfiche.directories?.primary?.directoryPath
    ) {
      return;
    }

    const meetingTeam = teams.find((team) => team.id === meeting.teamId);

    const meetingDocuments: Document[] = [];
    const seenDocumentIds = new Set<string>();
    for (const agendaModule of meeting.agenda?.agendaModules ?? []) {
      for (const agendaItem of agendaModule.agendaItems ?? []) {
        for (const doc of agendaItem.documents ?? []) {
          if (!doc) continue;
          if (doc.confidential) continue;

          const attachment = documentRepo.getBestAttachment(doc);
          if (!attachment) continue;

          if (seenDocumentIds.has(doc.id)) continue;

          seenDocumentIds.add(doc.id);
          meetingDocuments.push(doc);
        }
      }
    }

    const minutesDocument =
      documents?.find(
        (document) =>
          document.uploadType === "meeting-minutes" &&
          document.minutesData?.meetingId === meeting.id &&
          document.minutesData?.official
      ) ?? null;

    const pushedAgenda = laserfiche.metadata?.agendas?.enabled ? meeting : null;
    const pushedDocuments = laserfiche.metadata?.documents?.enabled
      ? meetingDocuments
      : null;
    const pushedMinutes = laserfiche.metadata?.minutes?.enabled
      ? minutesDocument
      : null;

    const { hostingType, repository, nickname } = laserfiche.connection;

    // build Laserfiche path:
    const basePath = laserfiche.directories.primary.directoryPath;
    const teamPart = isDefault
      ? (meetingTeam?.name ?? "Unknown Team") + "\\"
      : "";
    const meetingPart =
      meeting.name + " - " + formatDate(meeting.startTime, "yyyy-MM-dd");

    const path = `${basePath}\\${teamPart}${meetingPart}\\...`;

    setConfirmDetails(
      <>
        The meeting <em>{meeting?.name}</em> is selected for pushing to
        Laserfiche. The following documents will be pushed:
        <br />
        <br />
        <strong>
          {pushedAgenda ? (
            <>
              Agenda - {pushedAgenda.name}
              <br />
            </>
          ) : null}
          {pushedDocuments?.length
            ? pushedDocuments.map((document) => (
                <Fragment key={document.id}>
                  Document - {document.name}
                  <br />
                </Fragment>
              ))
            : null}
          {pushedMinutes ? (
            <>
              Minutes - {pushedMinutes.name}
              <br />
            </>
          ) : null}
        </strong>
        <br />
        They will be pushed to the{" "}
        <em>{hostingType === "self" ? "self-hosted" : "cloud-hosted"}</em>{" "}
        Laserfiche account, repository <em>{repository}</em>
        {nickname ? `, with the login nicknamed '${nickname}'` : null}, under
        the path:
        <br />
        <code>{path}</code>
        <br />
      </>
    );

    setConfirmOpen(true);
  });

  const handlePushMeeting = useFutureCallback(async () => {
    if (!tenantId || !meetingId) return;

    const errors = await meetingService.sendMeetingToLaserfiche({
      tenantId,
      meetingId,
    });

    if (errors) {
      setErrors(errors);
      setErrorsOpen(true);
    }
  });

  return (
    <div className="flex flex-col gap-2">
      <ConfirmDialog
        open={confirmOpen}
        setOpen={setConfirmOpen}
        title="Confirm Send Meeting to Laserfiche"
        message={
          <>
            {confirmDetails}
            <br />
            Are you sure you want to continue?
          </>
        }
        confirmLabel="Continue"
        onSubmit={handlePushMeeting}
      />
      <ErrorsDialog
        title="Errors While Pushing Meeting to Laserfiche"
        open={errorsOpen}
        setOpen={setErrorsOpen}
        errors={errors}
      />

      <SelectInput
        label="Narrow Meetings by Team"
        value={teamId}
        onValueChange={(teamId) => {
          if (teamId !== "all" && teamId !== meeting?.teamId) setMeetingId("");
          setTeamId(teamId);
        }}
        options={[
          <SelectItem key="all" value="all">
            All Teams
          </SelectItem>,
          ...(teams?.map((team) => (
            <SelectItem key={team.id} value={team.id}>
              {team.name}
            </SelectItem>
          )) ?? []),
        ]}
      />

      <SelectInput
        label="Meeting"
        value={meetingId}
        onValueChange={(meetingId) => setMeetingId(meetingId)}
        options={[
          ...(filteredMeetings.map((meeting) => (
            <SelectItem key={meeting.id} value={meeting.id}>
              {meeting.name} (
              {formatTZ(
                meeting.startTime,
                DATETIME_FORMAT,
                tenant?.timeZone ?? "America/New_York"
              )}
              )
            </SelectItem>
          )) ?? []),
        ]}
      />

      <div className="flex flex-row gap-2 justify-start mt-4">
        <Button
          disabled={
            !meeting ||
            !laserfiche?.connection ||
            !laserfiche?.directories?.primary?.directoryPath
          }
          tooltip={
            !meeting
              ? "Pick a meeting"
              : !laserfiche?.connection
                ? "Laserfiche connection not configured"
                : !laserfiche?.directories?.primary?.directoryPath
                  ? "No Laserfiche directory selected"
                  : "Push this meeting"
          }
          onClick={handleOpenConfirm}
        >
          <ArrowBigRightDashIcon className="mr-2" /> Push Meeting to Laserfiche
        </Button>
      </div>
    </div>
  );
};
